import { computed, inject, Injectable, signal } from '@angular/core';

import {
    cableRegex,
    viewIdRegex,
} from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.constants';
import {
    Cassettes,
    Drawers,
    SynopticPlanItem,
} from '@connect-services/client/components/synoptic/synoptic-plan/plan-core/synoptic-plan.models';
import { SynopticPlanBuilderService } from '@connect-services/client/services/synoptic/synoptic-plan-builder.service';
import { SynopticPlanRendererService } from '@connect-services/client/services/synoptic/synoptic-plan-renderer.service';
import { PlanTypeEnum, SynopticBayPlanItem, SynopticBoxPlanItem } from '@connect-services/src/sdk/api';

@Injectable({
    providedIn: 'root',
})
export class SynopticPlanInteractionService {
    public readonly cassetteCount = signal('');
    public readonly cassettes = signal<Cassettes<SynopticPlanItem>>({});
    public readonly cassettesStore = computed<Drawers | Cassettes<SynopticBoxPlanItem>>(() => {
        const items = this.planItems();

        if (!items.length) {
            return {};
        }

        const type = this.planType();

        return type === PlanTypeEnum.BAY
            ? this.synopticPlanBuilderService.groupByDrawer(items as SynopticBayPlanItem[])
            : (this.synopticPlanBuilderService.groupByCassette(items) as Cassettes<SynopticBoxPlanItem>);
    });
    public readonly currentPage = computed<number>(() => {
        const drawers = this.cassettesStore() as Drawers;
        const keys = Object.keys(drawers);
        const currentDrawerValue = this.drawerValue() || '';

        return keys.indexOf(currentDrawerValue);
    });
    public readonly drawerValue = signal<string | null>(null);
    public readonly isPlanRefreshNeeded = signal<boolean>(false);
    public readonly planItems = signal<SynopticPlanItem[]>([]);
    public readonly planType = signal<PlanTypeEnum | null>(null);
    public readonly selectedCassetteKey = signal<string | null>(null);

    private readonly synopticPlanBuilderService = inject(SynopticPlanBuilderService);
    private readonly synopticPlanRendererService = inject(SynopticPlanRendererService);

    public removeFocusOnFibreAndCables($event: MouseEvent): void {
        const stylesheet = (document.querySelector('#styleSheetEl') as HTMLStyleElement).sheet as CSSStyleSheet;

        const rules: CSSRule[] = Object.values(stylesheet.rules);

        if (!viewIdRegex.test(($event.target as HTMLElement)?.id) || !rules.length) {
            return;
        }

        const fibresRule = rules.find((rule) => (rule as CSSStyleRule).selectorText === '.jtk-connector path');

        if (fibresRule) {
            const container = document.querySelector('#boxPlan_container') as HTMLElement;
            const focusedFibre = container.querySelectorAll('.boxPlan__fibre__focused');
            if (focusedFibre.length) {
                focusedFibre.forEach((el) => {
                    el.classList.remove('boxPlan__fibre__focused');
                });
            }
            stylesheet.deleteRule(rules.indexOf(fibresRule));
            document.querySelector('.jtk-connector-focused')?.classList.remove('jtk-connector-focused');
        }

        const cableRule1: CSSRule | undefined = rules.find(
            (rule) => (rule as CSSStyleRule).selectorText === '.boxPlan__cableTubeGroup',
        );
        const cableRule2: CSSRule | undefined = rules.find((rule) =>
            cableRegex.test((rule as CSSStyleRule).selectorText),
        );

        if (cableRule1 || cableRule2) {
            this.synopticPlanRendererService.removeCableFocus(stylesheet);
        }
    }

    public selectCassetteByKey(key: string): void {
        this.selectedCassetteKey.set(key);
        this.displayFilteredCassette(key);
    }

    public updateDrawerPage(direction: string): void {
        const drawers = this.cassettesStore() as Drawers;

        const keys = Object.keys(drawers);
        const currentPage = this.currentPage();

        let targetPage;
        if (direction === 'next' || direction === 'prev') {
            targetPage = direction === 'next' ? currentPage + 1 : currentPage - 1;
        } else {
            targetPage = keys.indexOf(direction);
        }

        const drawerKey = Object.keys(drawers)[targetPage];
        const nextCassette = drawers[drawerKey];

        this.synopticPlanRendererService.resetPlanHtml();
        this.synopticPlanRendererService.jsPlumbInstances = {};
        this.drawerValue.set(drawerKey);

        this.cassettes.set(nextCassette.cassettes);

        this.isPlanRefreshNeeded.set(true);
    }

    private displayFilteredCassette(value: string, targetElement?: HTMLElement): void {
        if (!value || value === '-1') {
            return;
        }

        const nodeType = this.planType();
        let element = document.querySelector(`#plan_cassette_${value}`);

        if (nodeType === PlanTypeEnum.BAY && !element) {
            // Builds appropriate drawer view
            const target: HTMLElement = targetElement
                ? targetElement
                : (document.querySelector('#boxPlan_filter') as HTMLElement);

            const option = target.querySelector(`option[value=${value}]`) as HTMLOptionElement;
            const direction = option.dataset['drawer'] as string;

            this.updateDrawerPage(direction);

            element = document.querySelector(`#plan_cassette_${value}`);
        }

        if (!element) {
            return;
        }
        // Needs the timeout or does not run with bay
        setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        }, 0);

        setTimeout(() => {
            element.parentElement?.classList.add('boxPlan__viewFadeInOut');

            setTimeout(() => {
                element.parentElement?.classList.remove('boxPlan__viewFadeInOut');
            }, 2000);
        }, 400);
    }
}
